import type { Profile } from "../fs/profile";
import type { Vacancy } from "../fs/vacancies";

const SYNONYM_GROUPS: string[][] = [
  ["react", "reactjs", "react.js"],
  ["postgres", "postgresql", "postgres-db"],
  ["dotnet", ".net", "csharp", "c#"],
  ["devops", "ci/cd", "ci-cd", "pipelines", "jenkins", "gitlab ci", "github actions"],
  ["typescript", "ts"],
  ["javascript", "js", "ecmascript"],
  ["kubernetes", "k8s"],
  ["docker", "docker-compose", "containers"],
  ["golang", "go"],
  ["aws", "amazon web services"],
  ["azure", "microsoft azure"],
  ["gcp", "google cloud", "google cloud platform"],
  ["scrum", "agile", "kanban"],
];

function getLevenshteinDistance(a: string, b: string): number {
  const tmp = [];
  let i, j, val;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  for (i = 0; i <= a.length; i++) tmp[i] = [i];
  for (j = 0; j <= b.length; j++) tmp[0][j] = j;
  for (i = 1; i <= a.length; i++) {
    for (j = 1; j <= b.length; j++) {
      val = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      tmp[i][j] = Math.min(
        tmp[i - 1][j] + 1,
        tmp[i][j - 1] + 1,
        tmp[i - 1][j - 1] + val
      );
    }
  }
  return tmp[a.length][b.length];
}

function isFuzzyMatch(reqText: string, candidateSkill: string): boolean {
  const cleanReq = reqText.toLowerCase().trim();
  const cleanSkill = candidateSkill.toLowerCase().trim();

  // Direct containment check (e.g. if req contains skill name)
  if (cleanReq.includes(cleanSkill) || cleanSkill.includes(cleanReq)) {
    return true;
  }

  // Check synonym groups
  for (const group of SYNONYM_GROUPS) {
    const reqInGroup = group.some(alias => cleanReq.includes(alias));
    const skillInGroup = group.some(alias => cleanSkill.includes(alias));
    if (reqInGroup && skillInGroup) {
      return true;
    }
  }

  // Levenshtein distance check for spelling variations (only for words > 4 chars)
  if (cleanReq.length > 4 && cleanSkill.length > 4) {
    const words1 = cleanReq.split(/\s+/);
    const words2 = cleanSkill.split(/\s+/);
    for (const w1 of words1) {
      for (const w2 of words2) {
        if (w1.length > 4 && w2.length > 4) {
          const maxLen = Math.max(w1.length, w2.length);
          const dist = getLevenshteinDistance(w1, w2);
          if (dist / maxLen <= 0.25) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

function extractRequiredYears(requirementText: string): number {
  const clean = requirementText.toLowerCase();
  const match = clean.match(/(\d+)\s*(?:years?|jaar|yrs?|yr)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return 0;
}

export interface PreMatchResult {
  score: "strong" | "good" | "partial" | "weak" | "no_fit";
  matchedSkills: string[];
  missingSkills: string[];
}

export function calculatePreMatch(
  profile: Profile,
  vacancy: Vacancy
): PreMatchResult {
  const allCandidateSkills = [
    ...(profile.confirmed_skills || []).map(s => ({ name: s.skill, years: s.years })),
    ...(profile.inferred_skills || []).map(s => ({ name: s.skill, years: 1 }))
  ];

  const hardReqs = vacancy.requirements?.hard || [];
  const prefReqs = vacancy.requirements?.preferred || [];
  const competencies = vacancy.requirements?.competencies || [];
  const allVacancyRequirements = [...hardReqs, ...prefReqs, ...competencies];

  if (allVacancyRequirements.length === 0) {
    return { score: "no_fit", matchedSkills: [], missingSkills: [] };
  }

  let hardMatched = 0;
  let totalHard = hardReqs.length;
  let otherMatched = 0;
  let totalOther = prefReqs.length + competencies.length;

  const matchedSkills: string[] = [];
  const missingSkills: string[] = [];

  // Evaluate hard requirements
  for (const req of hardReqs) {
    const requiredYears = extractRequiredYears(req);
    let found = false;
    for (const skill of allCandidateSkills) {
      if (isFuzzyMatch(req, skill.name)) {
        if (requiredYears > 0 && skill.years < requiredYears) {
          hardMatched += 0.5;
        } else {
          hardMatched += 1;
        }
        matchedSkills.push(req);
        found = true;
        break;
      }
    }
    if (!found) {
      missingSkills.push(req);
    }
  }

  // Evaluate preferred / competencies requirements
  for (const req of [...prefReqs, ...competencies]) {
    const requiredYears = extractRequiredYears(req);
    let found = false;
    for (const skill of allCandidateSkills) {
      if (isFuzzyMatch(req, skill.name)) {
        if (requiredYears > 0 && skill.years < requiredYears) {
          otherMatched += 0.5;
        } else {
          otherMatched += 1;
        }
        matchedSkills.push(req);
        found = true;
        break;
      }
    }
    if (!found) {
      missingSkills.push(req);
    }
  }

  // Calculate final scores
  const hardRatio = totalHard > 0 ? hardMatched / totalHard : 1;
  const otherRatio = totalOther > 0 ? otherMatched / totalOther : 1;

  // Weight: 70% hard requirements, 30% preferred/competencies
  let finalScore = 0;
  if (totalHard > 0 && totalOther > 0) {
    finalScore = (hardRatio * 0.7) + (otherRatio * 0.3);
  } else if (totalHard > 0) {
    finalScore = hardRatio;
  } else if (totalOther > 0) {
    finalScore = otherRatio;
  }

  let score: "strong" | "good" | "partial" | "weak" | "no_fit" = "no_fit";
  if (finalScore >= 0.75) {
    score = "strong";
  } else if (finalScore >= 0.5) {
    score = "good";
  } else if (finalScore >= 0.25) {
    score = "partial";
  } else if (finalScore > 0) {
    score = "weak";
  }

  return { score, matchedSkills, missingSkills };
}
